/* global.js — progressive enhancement custom elements. Vanilla, no globals. */

/* --- Reveal on scroll -----------------------------------------------------
   FIRST in the file on purpose. Everything carrying `.reveal` starts at
   opacity 0 and only this observer clears it, so while this sat two thirds of
   the way down, a throw in ANY feature above it — the gallery, the steppers,
   a custom element's connectedCallback — took the whole homepage with it. The
   page going blank is a far worse failure than one broken widget, so the code
   that makes content visible now runs before anything that could prevent it.

   (Scripting switched off entirely is handled in CSS instead; see the
   `.no-js .reveal` rule in base.css.) ------------------------------------- */
(function initReveal() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const elements = document.querySelectorAll('.reveal');
  if (!elements.length) return;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    elements.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries, obs) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  elements.forEach((el) => observer.observe(el));
})();


/**
 * <header-nav> — mobile off-canvas menu (toggle, overlay, close button,
 * link-close, scroll lock) plus the "Shop" mega menu (hover w/ close delay
 * on desktop, tap-accordion on mobile — same markup, CSS + this class just
 * switch how it opens). Enhances the header markup rendered by
 * sections/header.liquid. Works without JS (nav is visible on desktop).
 */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-header-toggle]');
    this.close = this.querySelector('[data-header-close]');
    this.overlay = this.querySelector('[data-header-overlay]');
    this.nav = this.querySelector('[data-header-nav]');
    if (!this.toggle || !this.nav) return;

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.toggle.addEventListener('click', this.onToggleClick);
    if (this.close) this.close.addEventListener('click', this.onCloseClick);
    if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
    this.nav.addEventListener('click', this.onNavClick);
    this.addEventListener('keydown', this.onKeydown);

    this.initDropdowns();
  }

  disconnectedCallback() {
    if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
    if (this.close) this.close.removeEventListener('click', this.onCloseClick);
    if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
    if (this.nav) this.nav.removeEventListener('click', this.onNavClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onToggleClick() {
    const open = this.getAttribute('data-open') === 'true';
    this.setOpen(!open);
  }

  onCloseClick() {
    this.setOpen(false);
  }

  onOverlayClick() {
    this.setOpen(false);
  }

  onNavClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (link) this.setOpen(false);
  }

  setOpen(open) {
    this.setAttribute('data-open', String(open));
    this.toggle.setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      const firstLink = this.nav.querySelector('a, button');
      if (firstLink) firstLink.focus();
    } else {
      /* Every trigger, not just the first: with nested Admin submenus there is
         now more than one, and closing the off-canvas panel while a submenu was
         expanded left it expanded behind the scrim. */
      this.querySelectorAll('[data-mega-trigger]').forEach((trigger) => {
        trigger.setAttribute('aria-expanded', 'false');
      });
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.getAttribute('data-open') === 'true') {
      this.setOpen(false);
      this.toggle.focus();
    }
  }

  /**
   * Dropdown nav items: a <button data-mega-trigger> + sibling
   * <div data-mega-panel>, inside a [data-mega-item]. Click always toggles
   * (this is what makes it work as a mobile accordion). On top of that,
   * desktop pointer users get hover-to-open with a ~200ms close delay so the
   * cursor can travel from the nav link down into the panel without it
   * flickering shut.
   *
   * Drives both the hardcoded "Shop" mega panel and any nested menu the
   * merchant builds in Admin — same markup contract, so one implementation.
   * Previously this bound only the FIRST match, so a second dropdown was inert.
   */
  initDropdowns() {
    this.querySelectorAll('[data-mega-item]').forEach((item) => this.bindDropdown(item));
  }

  bindDropdown(item) {
    const trigger = item.querySelector('[data-mega-trigger]');
    if (!trigger) return;

    let closeTimer = null;
    const cancelClose = () => {
      if (closeTimer) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
    };
    const openMega = () => {
      cancelClose();
      trigger.setAttribute('aria-expanded', 'true');
    };
    const closeMegaNow = () => trigger.setAttribute('aria-expanded', 'false');
    const scheduleClose = () => {
      cancelClose();
      closeTimer = setTimeout(closeMegaNow, 200);
    };

    // Hover/focus auto-open is a desktop-mouse enhancement layered on top of
    // click-to-toggle. A touch tap fires a synthetic mouseenter + focus
    // before its click, so binding these unconditionally opens the panel via
    // mouseenter/focusin and then the click handler's toggle immediately
    // closes it again — the panel never stays visible on touch. Restrict
    // hover/focus auto-open to devices whose primary input can actually hover.
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      item.addEventListener('mouseenter', openMega);
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', openMega);
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) closeMegaNow();
      });
    }

    trigger.addEventListener('click', () => {
      cancelClose();
      const open = trigger.getAttribute('aria-expanded') === 'true';
      /* Close any sibling dropdown first — two open panels overlap each other
         on desktop, and on mobile they push the rest of the nav off screen. */
      if (!open) {
        this.querySelectorAll('[data-mega-trigger]').forEach((other) => {
          if (other !== trigger) other.setAttribute('aria-expanded', 'false');
        });
      }
      trigger.setAttribute('aria-expanded', String(!open));
    });
  }
}

if (!customElements.get('header-nav')) customElements.define('header-nav', HeaderNav);
/* <cart-drawer> is defined in assets/cart.js, which owns all cart AJAX logic. */

/**
 * <variant-selects> — resolves the chosen option combination to a variant and
 * brings the rest of the page into line with it.
 *
 * The select elements inside carry no `name`; they are UI. The single source of
 * truth for what gets added to the cart is the hidden `[data-variant-id]` input
 * in the product form, which Liquid renders `disabled` (see
 * snippets/variant-picker.liquid for why) and which this element enables on
 * connect — so with JS off the <noscript> select is the only live control, and
 * with JS on this one is.
 *
 * Price, the buy button and the sticky bar are updated by re-rendering the
 * section server-side through the Section Rendering API rather than by writing
 * text from the variant JSON. That keeps `money` formatting, the sale badge,
 * unit pricing and the Arabic numeral formatting authoritative in Liquid; the
 * alternative is reimplementing currency formatting per locale in JS and
 * getting it subtly wrong on /ar.
 */
class VariantSelects extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-variant-data]');
    if (!data) return;
    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      return; /* Malformed payload: leave the <noscript> path as the only one. */
    }

    this.form = document.getElementById(this.dataset.form);
    this.idInput = this.form && this.form.querySelector('[data-variant-id]');
    /* This is what hands control to the JS path. Until it runs, the form posts
       whatever the <noscript> select holds. */
    if (this.idInput) this.idInput.disabled = false;

    this.onChange = this.onChange.bind(this);
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  get selectedValues() {
    return Array.from(this.querySelectorAll('select[data-option-index]'))
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex))
      .map((select) => select.value);
  }

  onChange() {
    const chosen = this.selectedValues;
    /* `options` on a variant is positional, so compare index for index. A
       combination that does not exist (a real possibility as soon as there are
       two options) yields undefined — that is the "Unavailable" state, not an
       error. */
    const variant = this.variants.find(
      (candidate) => candidate.options.length === chosen.length
        && candidate.options.every((value, index) => value === chosen[index])
    );

    this.updateVariantId(variant);
    this.updateUrl(variant);
    this.updateMedia(variant);
    this.renderSection(variant);
  }

  updateVariantId(variant) {
    if (!this.idInput) return;
    if (variant) {
      this.idInput.value = variant.id;
      this.idInput.disabled = false;
    } else {
      /* Nothing to add: disable rather than leave a stale id that would put the
         wrong variant in the cart. */
      this.idInput.disabled = true;
    }
  }

  updateUrl(variant) {
    if (!variant || !window.history.replaceState) return;
    /* replaceState, not pushState: a shopper cycling options should not have to
       press Back once per click to leave the page. The param still makes the
       URL shareable and survives a reload. */
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateMedia(variant) {
    const mediaId = variant && variant.featured_media && variant.featured_media.id;
    if (!mediaId) return;
    document.dispatchEvent(
      new CustomEvent('variant:media', { detail: { mediaId: String(mediaId) } })
    );
  }

  async renderSection(variant) {
    const sectionId = this.dataset.section;
    const baseUrl = this.dataset.url;
    if (!sectionId || !baseUrl) return;

    const query = variant ? `variant=${variant.id}&` : '';
    try {
      const response = await fetch(`${baseUrl}?${query}section_id=${sectionId}`);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      ['[data-price-container]', '[data-buy-buttons]', '[data-sticky-price]'].forEach((selector) => {
        const next = doc.querySelector(selector);
        const current = document.querySelector(selector);
        if (next && current) current.innerHTML = next.innerHTML;
      });
      /* The sticky bar's button lives outside [data-buy-buttons] (it is a
         sibling of the grid, not part of it), so mirror its disabled state
         from the freshly rendered in-page one. */
      const freshAdd = doc.querySelector('[data-add-button]');
      const stickyAdd = document.querySelector('[data-sticky-add-button]');
      if (freshAdd && stickyAdd) {
        stickyAdd.disabled = freshAdd.disabled;
        stickyAdd.textContent = freshAdd.textContent.trim();
      }
    } catch (error) {
      /* Network hiccup: the id input is already correct, so the form still adds
         the right variant — only the displayed price lags. Better than blanking
         the buy button. */
    }
  }
}

if (!customElements.get('variant-selects')) customElements.define('variant-selects', VariantSelects);

/**
 * <facet-filters> — in-place filtering, sorting and pagination for the
 * collection and search grids.
 *
 * The markup it wraps (snippets/facets.liquid) is a plain GET form that already
 * works: tick, submit, land on a filtered URL. This element intercepts that and
 * fetches the same URL through the Section Rendering API instead, swapping the
 * filter bar and the results grid rather than reloading the document. Any
 * failure falls through to a real navigation, so the worst case is the
 * behaviour we started with.
 *
 * The filter bar is re-rendered too, not just the grid — the per-value result
 * counts and which values are still reachable both change with every selection,
 * and a stale bar showing "(12)" next to an option that now yields nothing is
 * worse than no counts at all.
 */
class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    /* Pagination lives in the results container, which is a sibling — this
       element only wraps the controls. */
    this.results = document.querySelector('[data-facet-results]');
    if (this.results) this.results.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    /* The element replaces its own subtree, not itself, so this only runs on a
       real teardown (theme editor section removal). Still: leaving a popstate
       listener bound to a detached node is how you get two handlers racing
       after an editor reload. */
    window.removeEventListener('popstate', this.onPopState);
    if (this.results) this.results.removeEventListener('click', this.onClick);
  }

  onChange() {
    this.apply(this.formUrl());
  }

  onClick(event) {
    const link = event.target.closest('[data-facet-link], .pagination__link');
    if (!link || !link.href) return;
    event.preventDefault();
    this.apply(link.href);
  }

  onPopState() {
    /* Back/forward should re-render, not re-navigate; render from whatever URL
       the browser just restored. */
    this.apply(window.location.href, false);
  }

  /** The form's current state as an absolute URL. */
  formUrl() {
    const form = this.querySelector('[data-facet-form]');
    const url = new URL(form.action, window.location.origin);
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      /* Empty price bounds must not be sent: `filter.v.price.gte=` is a filter
         for "price at least nothing", which Shopify reads as an active filter
         and which then shows up as a removable chip the shopper never set. */
      if (String(value).trim() !== '') params.append(key, value);
    });
    url.search = params.toString();
    return url.toString();
  }

  async apply(url, updateHistory = true) {
    const grid = document.querySelector('[data-facet-results]');
    if (grid) grid.dataset.loading = 'true';

    /* Re-focusing by id after the swap is what keeps the keyboard usable: the
       control the shopper just operated is destroyed by the re-render, and
       without this focus resets to <body> on every tick. */
    const activeId = document.activeElement && document.activeElement.id;
    const openGroups = Array.from(this.querySelectorAll('.facets__group')).map((d) => d.open);

    try {
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.sectionId);
      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(String(response.status));

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const nextFacets = doc.querySelector('facet-filters');
      const nextResults = doc.querySelector('[data-facet-results]');
      if (!nextResults) throw new Error('no results in response');

      if (nextFacets) this.innerHTML = nextFacets.innerHTML;
      if (grid) {
        grid.innerHTML = nextResults.innerHTML;
        delete grid.dataset.loading;
      }

      /* Restore what the re-render cannot know about: which panels the shopper
         had expanded, and where the caret was. */
      this.querySelectorAll('.facets__group').forEach((details, index) => {
        if (openGroups[index]) details.open = true;
      });
      if (activeId) {
        const restored = this.querySelector(`#${CSS.escape(activeId)}`);
        if (restored) restored.focus();
      }

      if (updateHistory) window.history.pushState({ facets: true }, '', url);
    } catch (error) {
      /* Give up on the enhancement, not on the shopper's click. */
      window.location.href = url;
    }
  }
}

if (!customElements.get('facet-filters')) customElements.define('facet-filters', FacetFilters);

/**
 * <predictive-search> — header typeahead.
 *
 * The toggle it wraps is a real <a href="/search">, and the panel contains a
 * real GET form pointed at the same place, so with JS off the control is a link
 * to the search page and nothing is lost. Everything below is enhancement.
 *
 * Results come from Shopify's Predictive Search endpoint rendered through
 * sections/predictive-search.liquid, so the markup — money formatting, locale,
 * translated headings — is built server-side rather than assembled from JSON in
 * the browser.
 *
 * Keyboard model is the ARIA combobox: focus never leaves the input, and the
 * arrow keys move `aria-activedescendant` between option ids. That is what lets
 * someone keep typing to refine after arrowing down, which moving real focus
 * into the list would prevent.
 */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('[data-search-toggle]');
    this.panel = this.querySelector('[data-search-panel]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.closeBtn = this.querySelector('[data-search-close]');
    if (!this.toggle || !this.panel || !this.input || !this.results) return;

    this.activeIndex = -1;
    this.debounceTimer = null;
    this.controller = null;
    /* Shopify's Predictive Search API does not support every storefront
       locale. On an unsupported one — Arabic, for this shop — it answers 417
       "Unsupported buyer locale" to every request, forever. Latch that on the
       first occurrence and stop asking: without this we fire a doomed request
       on every keystroke for the whole session. The panel keeps working as a
       plain search form, which is the right experience there regardless. */
    this.predictiveUnavailable = false;

    this.onToggle = this.onToggle.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.close = this.close.bind(this);

    this.toggle.addEventListener('click', this.onToggle);
    this.input.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKeydown);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.close);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.debounceTimer);
    if (this.controller) this.controller.abort();
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  onToggle(event) {
    /* The href stays a working link for the no-JS case and for middle-click /
       cmd-click, which we must not swallow. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.input.focus();
    this.input.select();
  }

  close() {
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.clearResults();
    this.toggle.focus();
  }

  onDocumentClick(event) {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) {
      /* Not this.close(): pulling focus back to the toggle because someone
         clicked elsewhere on the page would yank the caret out of whatever they
         were actually reaching for. */
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.clearResults();
    }
  }

  onInput() {
    const term = this.input.value.trim();
    clearTimeout(this.debounceTimer);

    /* One character matches most of the catalogue and the result is noise, so
       the request is not worth making. */
    if (term.length < 2) {
      this.clearResults();
      return;
    }

    if (this.predictiveUnavailable) return;

    /* 250ms: long enough that a normal typing run issues one request rather
       than one per keystroke, short enough not to feel laggy after a pause. */
    this.debounceTimer = setTimeout(() => this.search(term), 250);
  }

  async search(term) {
    /* Abort the previous request rather than letting both land — responses can
       arrive out of order, and a slow "sn" resolving after a fast "snail" would
       replace the correct results with stale ones. */
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    this.dataset.loading = 'true';

    const params = new URLSearchParams({
      q: term,
      'resources[type]': 'product,query,collection,article,page',
      'resources[limit]': '6',
      'resources[options][unavailable_products]': 'last',
      section_id: 'predictive-search',
    });

    try {
      const response = await fetch(`${this.dataset.predictiveUrl}?${params}`, {
        signal: this.controller.signal,
      });
      if (response.status === 417) {
        /* Unsupported locale — see predictiveUnavailable above. Not an error
           worth surfacing: the shopper still has a working search form. */
        this.predictiveUnavailable = true;
        this.clearResults();
        return;
      }
      if (!response.ok) throw new Error(String(response.status));

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      /* The section renders bare — no wrapper of its own — so take the body's
         children rather than looking for a container that is not there. */
      const rendered = doc.querySelector('#shopify-section-predictive-search') || doc.body;

      this.results.innerHTML = rendered.innerHTML;
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');

      const count = this.results.querySelector('[data-predictive-count]');
      if (this.status && count) this.status.textContent = count.textContent.trim();
    } catch (error) {
      /* AbortError is the normal path for superseded keystrokes, not a fault —
         and the request that replaced this one owns the UI now, so leave the
         loading flag to it. */
      if (error.name === 'AbortError') return;
      this.clearResults();
    } finally {
      if (!this.controller || !this.controller.signal.aborted) {
        delete this.dataset.loading;
      }
    }
  }

  clearResults() {
    clearTimeout(this.debounceTimer);
    this.results.innerHTML = '';
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    if (this.status) this.status.textContent = '';
    delete this.dataset.loading;
  }

  get options() {
    return Array.from(this.results.querySelectorAll('[data-predictive-option]'));
  }

  onKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const options = this.options;
    if (options.length === 0) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      /* Wraps at both ends, and starts at the first item when nothing is
         selected regardless of direction — arrowing up from the input should
         reach the list, not do nothing. */
      const next = this.activeIndex === -1
        ? (delta === 1 ? 0 : options.length - 1)
        : (this.activeIndex + delta + options.length) % options.length;
      this.setActive(next);
      return;
    }

    if (event.key === 'Enter' && this.activeIndex > -1) {
      /* Only intercept Enter when an option is highlighted; otherwise the form
         submits to the full search page, which is the expected behaviour. */
      event.preventDefault();
      options[this.activeIndex].click();
    }
  }

  setActive(index) {
    const options = this.options;
    options.forEach((option, i) => {
      option.setAttribute('aria-selected', String(i === index));
    });
    this.activeIndex = index;
    const active = options[index];
    if (!active) return;
    this.input.setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

/* --- Quantity inputs: auto-submit the closest cart form on change --------- */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement) || !input.matches('[data-quantity-input]')) return;
  const form = input.closest('form');
  if (form && typeof form.requestSubmit === 'function') {
    form.requestSubmit();
  }
});

/* --- Quantity steppers: +/- buttons adjust the sibling number input ------- */
document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const button = target && target.closest('[data-quantity-decrease], [data-quantity-increase]');
  if (!button) return;
  const wrapper = button.closest('[data-quantity-wrapper]');
  const input = wrapper && wrapper.querySelector('input[type="number"]');
  if (!input) return;

  /* `Number('0') || 1` collapsed a legitimate min of 0 back to 1, which meant
     the minus button on the cart page could never reach 0 — the value Shopify
     reads as "remove this line". Only fall back when min is actually absent. */
  const min = input.min === '' ? 1 : Number(input.min);
  const step = Number(input.step) || 1;
  const current = Number(input.value) || min;
  const next = button.matches('[data-quantity-decrease]') ? Math.max(min, current - step) : current + step;

  input.value = next;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* --- Product gallery: thumbnail click swaps the active main image; on
   mobile the main track is a native CSS scroll-snap strip, so an
   IntersectionObserver keeps the thumbnails/dots in sync with swipes. ----- */
(function initProductGalleries() {
  const galleries = document.querySelectorAll('[data-product-gallery]');
  if (!galleries.length) return;

  galleries.forEach((gallery) => {
    const track = gallery.querySelector('[data-gallery-main-track]');
    const mainItems = gallery.querySelectorAll('[data-gallery-main-item]');
    const thumbnails = gallery.querySelectorAll('[data-gallery-thumbnail]');
    const dots = gallery.querySelectorAll('[data-gallery-dot]');

    function setActive(mediaId) {
      mainItems.forEach((item) => item.classList.toggle('is-active', item.dataset.mediaId === mediaId));
      thumbnails.forEach((thumb) => thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId));
      dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.mediaId === mediaId));
    }

    thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => setActive(thumb.dataset.mediaId));
    });

    /* Variant changes carry a featured image; jump the gallery to it. On
       desktop the class swap is enough (one item is shown at a time), but the
       mobile track is a native scroll-snap strip where classes move nothing —
       so nudge scrollLeft by the measured delta. Measuring the delta rather
       than assigning offsetLeft is what keeps this correct in RTL, where
       scrollLeft runs negative in some engines. */
    document.addEventListener('variant:media', (event) => {
      const mediaId = event.detail && event.detail.mediaId;
      if (!mediaId) return;
      const target = gallery.querySelector(`[data-gallery-main-item][data-media-id="${mediaId}"]`);
      if (!target) return;
      setActive(mediaId);
      if (track && track.scrollWidth > track.clientWidth) {
        track.scrollLeft += target.getBoundingClientRect().left - track.getBoundingClientRect().left;
      }
    });

    if (track && mainItems.length > 1 && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(entry.target.dataset.mediaId);
          });
        },
        { root: track, threshold: 0.6 }
      );
      mainItems.forEach((item) => observer.observe(item));
    }
  });
})();

/* --- Sticky header: flag the stuck state -----------------------------------
   Past ~100px the header picks up its hairline and, on mobile, condenses to
   ~55px. Desktop keeps the full nav — the condensed rules live inside the
   max-width:749px block in sections/header.liquid.

   Two things keep the flip from jittering:

   1. Hysteresis. It condenses past STUCK_AFTER but only expands again above
      UNSTUCK_BEFORE, so momentum, rubber-banding, or the browser's own
      scroll-anchoring nudges around the threshold can't toggle it twice.

   2. A fixed placeholder. The header's expanded height is measured while it
      is at rest and written to --header-expanded-height on the section
      wrapper, which header.liquid uses as a min-block-size on mobile. The
      header then condenses inside a box that never changes size, so nothing
      below it reflows and scroll anchoring has nothing to correct. Measured
      only at rest: a read taken mid-transition would capture a half-size
      header, so a resize that lands while stuck is deferred until the header
      has expanded again (transitionend, or a timer for the reduced-motion
      case where no transition runs and no event fires). ----------------- */
(function initStickyHeader() {
  const STUCK_AFTER = 100;
  const UNSTUCK_BEFORE = 40;
  let header = null;
  let wrapper = null;
  let stuck = false;
  let ticking = false;
  let needsMeasure = true;
  let measureTimer = 0;

  const measure = () => {
    if (!header || stuck) {
      needsMeasure = true;
      return;
    }
    needsMeasure = false;
    const height = header.offsetHeight;
    if (wrapper && height) wrapper.style.setProperty('--header-expanded-height', `${height}px`);
  };

  /* Wait out the expand transition before measuring, so the stored height
     is the header at rest rather than a frame of it growing. */
  const measureWhenSettled = () => {
    window.clearTimeout(measureTimer);
    const inner = header.querySelector('.header__inner') || header;
    const seconds = parseFloat(getComputedStyle(inner).transitionDuration) || 0;
    measureTimer = window.setTimeout(measure, seconds * 1000 + 50);
  };

  const update = () => {
    ticking = false;
    if (!header) return;
    const y = window.scrollY;
    const next = stuck ? y > UNSTUCK_BEFORE : y > STUCK_AFTER;
    if (next !== stuck) {
      stuck = next;
      header.setAttribute('data-stuck', String(stuck));
      if (!stuck && needsMeasure) measureWhenSettled();
    }
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  const bind = () => {
    header = document.querySelector('[data-sticky-header]');
    wrapper = header ? header.parentElement : null;
    stuck = false;
    needsMeasure = true;
    if (!header) return;
    /* First paint is always the expanded header — no data-stuck yet — so
       this read is safe even when the page loads already scrolled. */
    measure();
    update();
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    needsMeasure = true;
    if (!stuck) measure();
    onScroll();
  }, { passive: true });
  document.addEventListener('transitionend', (event) => {
    if (header && needsMeasure && !stuck && header.contains(event.target)) measure();
  });
  /* The theme editor re-renders the section, replacing the element. */
  document.addEventListener('shopify:section:load', bind);
  bind();
})();

/* --- Back to top -----------------------------------------------------------
   Fades in past two viewport heights. Smooth scroll, unless the visitor asks
   for reduced motion — then it jumps. */
(function initBackToTop() {
  const button = document.querySelector('[data-back-to-top]');
  if (!button) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let ticking = false;

  const update = () => {
    ticking = false;
    button.setAttribute('data-visible', String(window.scrollY > window.innerHeight * 2));
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  button.addEventListener('click', () => {
    window.scrollTo({ top: 0, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  });
  update();
})();

/* --- Sticky add-to-bag (mobile PDP) ----------------------------------------
   Shows once the main add-to-cart button has scrolled UP out of the viewport,
   and only then: while it is merely still below the fold on first paint the
   bar stays down, so the top of a product page isn't pre-loaded with a CTA the
   visitor hasn't reached yet.

   Deliberately a rect read on the same rAF-throttled scroll tick as the other
   two controls, not an IntersectionObserver: IO only fires when the
   intersection ratio CROSSES a threshold, so a jump straight past the button
   (in-page anchor, scroll restoration, a fling that skips frames) goes ratio
   0 -> ratio 0, no callback, and the bar never appears.

   `.has-sticky-atc` is added on load rather than on show so the document's
   reserved bottom padding never changes underfoot; `data-atc-bar` on the body
   is what lifts the back-to-top control clear of the bar. */
(function initStickyAddToCart() {
  const bar = document.querySelector('[data-sticky-atc]');
  if (!bar) return;

  /* Re-queried every tick rather than captured once: <variant-selects> swaps
     the whole [data-buy-buttons] subtree on a variant change, so a reference
     taken at load points at a detached node from then on and the bar freezes in
     whatever state it was in. */
  const getTrigger = () => document.querySelector('.main-product__form .main-product__submit');
  if (!getTrigger()) return;

  document.body.classList.add('has-sticky-atc');

  let ticking = false;
  const update = () => {
    ticking = false;
    const trigger = getTrigger();
    if (!trigger) return;
    const visible = trigger.getBoundingClientRect().bottom <= 0;
    bar.setAttribute('data-visible', String(visible));
    document.body.setAttribute('data-atc-bar', visible ? 'visible' : 'hidden');
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
})();

/* --- Product recommendations: swap the shell for the warm render ----------
   The PDP can't ask Shopify's recommendation engine for products at render
   time, so related-products.liquid ships an empty shell carrying the
   recommendations URL. Fetch that URL, pull the matching element out of the
   response, and replace the shell with it. Any failure leaves the shell in
   place, which renders as nothing — no error state to design for. --------- */
(function initProductRecommendations() {
  const containers = document.querySelectorAll('[data-related-products]');
  if (!containers.length) return;

  containers.forEach((container) => {
    const url = container.dataset.url;
    if (!url) return;

    fetch(url)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((markup) => {
        const fresh = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-related-products]');
        /* No element back means the engine returned nothing for this product;
           drop the shell so the page doesn't keep a dead heading. */
        if (!fresh) {
          container.remove();
          return;
        }
        container.replaceWith(fresh);
        /* The section carries `.reveal`, which starts at opacity 0 and waits
           for the scroll observer — but that observer ran at page load, long
           before this markup existed, and handing it a node the shopper has
           already scrolled past would leave the section invisible for good.
           This content arrives after first paint anyway, so reveal it outright
           rather than animating it in. */
        fresh.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      })
      .catch(() => {
        container.remove();
      });
  });
})();

/* --- Product image zoom ----------------------------------------------------
   Opens the <dialog> in main-product.liquid at whichever image was clicked.
   The dialog is native, so Escape, the focus trap and background inertness are
   the browser's job, not ours — all this owns is which image is showing.

   Positioning is done by scrollLeft delta rather than by index maths so it is
   correct in RTL, where the track's scroll origin is on the right and some
   engines report scrollLeft as negative. ----------------------------------- */
(function initProductZoom() {
  const dialog = document.querySelector('[data-zoom-dialog]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const track = dialog.querySelector('[data-zoom-track]');
  const items = Array.from(dialog.querySelectorAll('[data-zoom-item]'));
  const counter = dialog.querySelector('[data-zoom-counter]');
  if (!track || items.length === 0) return;

  let index = 0;

  const scrollToIndex = (next, behavior = 'auto') => {
    index = (next + items.length) % items.length;
    const target = items[index];
    const delta = target.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: delta, behavior });
    if (counter) counter.textContent = `${index + 1} / ${items.length}`;
  };

  document.querySelectorAll('[data-zoom-open]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const mediaId = trigger.dataset.mediaId;
      const found = items.findIndex((item) => item.dataset.mediaId === mediaId);
      dialog.showModal();
      /* After showModal, not before: the track has no layout while the dialog
         is closed, so every getBoundingClientRect would read 0 and the scroll
         delta would be meaningless. */
      scrollToIndex(found === -1 ? 0 : found);
    });
  });

  dialog.querySelectorAll('[data-zoom-close]').forEach((btn) => {
    btn.addEventListener('click', () => dialog.close());
  });

  const prev = dialog.querySelector('[data-zoom-prev]');
  const next = dialog.querySelector('[data-zoom-next]');
  if (prev) prev.addEventListener('click', () => scrollToIndex(index - 1, 'smooth'));
  if (next) next.addEventListener('click', () => scrollToIndex(index + 1, 'smooth'));

  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    /* Arrow keys follow the writing direction: in RTL, Left is "forward". */
    const rtl = document.documentElement.dir === 'rtl';
    const forward = rtl ? event.key === 'ArrowLeft' : event.key === 'ArrowRight';
    scrollToIndex(index + (forward ? 1 : -1), 'smooth');
  });

  /* Clicking the backdrop closes. The dialog element itself is the backdrop's
     hit target, so a click landing on it rather than on .product-zoom__inner
     means the shopper clicked outside the content. */
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  /* Keep the counter honest when the shopper swipes the track directly. */
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        index = items.indexOf(entry.target);
        if (counter) counter.textContent = `${index + 1} / ${items.length}`;
      });
    }, { root: track, threshold: 0.6 });
    items.forEach((item) => observer.observe(item));
  }
})();

/* --- Share -----------------------------------------------------------------
   navigator.share where it exists (all mobile browsers — this is what reaches
   WhatsApp), clipboard otherwise. Both are gated on a user gesture, which a
   click satisfies. An AbortError means the shopper dismissed the share sheet;
   that is a completed interaction, not a failure to report. ---------------- */
(function initShare() {
  document.querySelectorAll('[data-share]').forEach((button) => {
    const status = button.parentElement.querySelector('[data-share-status]');
    const url = button.dataset.shareUrl;

    button.addEventListener('click', async () => {
      if (navigator.share) {
        try {
          await navigator.share({ title: button.dataset.shareTitle, url });
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
          /* Anything else: fall through to the clipboard path below. */
        }
      }
      try {
        await navigator.clipboard.writeText(url);
        const message = button.dataset.shareCopied;
        button.textContent = message;
        if (status) status.textContent = message;
        setTimeout(() => { button.textContent = button.dataset.shareLabel; }, 2000);
      } catch (error) {
        /* Clipboard blocked (insecure context, or permission denied). Nothing
           useful to offer, and the URL is in the address bar regardless. */
      }
    });

    /* Stashed before the label is ever swapped out, so restoring it does not
       depend on having read it at the right moment. */
    button.dataset.shareLabel = button.textContent.trim();
  });
})();

/**
 * <announcement-bar> — fades between promo messages.
 *
 * Only rotates when the merchant asked for it AND there is more than one
 * message AND the visitor has not asked for reduced motion. Anything auto-
 * advancing in the corner of the eye is exactly what that preference is about,
 * so the reduced-motion path shows the first message and stops — it does not
 * merely swap the transition for an instant cut, which would still be movement.
 *
 * Pauses on hover and on focus-within: a message can contain a link, and having
 * it slide away mid-reach is how you click the wrong thing. Also pauses while
 * the tab is hidden, so returning to a backgrounded tab does not land on a
 * message that has cycled forty times against nobody.
 */
class AnnouncementBar extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-announcement-slide]'));
    if (this.slides.length === 0) return;

    /* Make the first slide visible even when not rotating — the CSS starts
       every slide hidden so the no-JS case can pick exactly one. */
    this.index = 0;
    this.show(0);

    if (!this.hasAttribute('data-autorotate') || this.slides.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.speed = Number(this.dataset.speed) || 5000;
    this.onVisibility = this.onVisibility.bind(this);

    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  onVisibility() {
    if (document.hidden) {
      this.stop();
    } else {
      this.start();
    }
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.show(this.index + 1), this.speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  show(next) {
    this.index = (next + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      /* aria-hidden as well as opacity: an invisible-but-present slide is still
         read out, so without this a screen reader announces every message on
         every rotation. */
      if (active) {
        slide.removeAttribute('aria-hidden');
      } else {
        slide.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

/* --- Recently viewed -------------------------------------------------------
   Records product ids on the PDP and, wherever the Recently viewed section is
   placed, swaps its empty shell for a warm render fetched through the search
   endpoint. See sections/recently-viewed.liquid for the two-part shape.

   The id list is capped and de-duplicated on write, so the store never grows
   without bound and re-viewing a product moves it to the front rather than
   adding a second entry. -------------------------------------------------- */
(function initRecentlyViewed() {
  const KEY = 'ekka:recently-viewed';
  const MAX = 12;

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY));
      return Array.isArray(raw) ? raw.filter((id) => Number.isInteger(id)) : [];
    } catch (error) {
      /* Corrupt or unavailable (Safari private mode throws on read too). Treat
         it as "nothing viewed" rather than letting it break the page. */
      return [];
    }
  };

  const record = (id) => {
    try {
      const next = [id, ...read().filter((existing) => existing !== id)].slice(0, MAX);
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch (error) {
      /* Storage full or blocked — this feature is not worth an exception. */
    }
  };

  const currentId = document.querySelector('[data-product-id]');
  if (currentId) record(Number(currentId.dataset.productId));

  document.querySelectorAll('[data-recently-viewed]').forEach((container) => {
    const limit = Number(container.dataset.limit) || 4;
    /* Exclude whatever page we are on: "recently viewed" listing the product
       currently on screen is noise. Done here rather than in Liquid because the
       warm render has no product context to compare against. */
    const currentValue = currentId ? Number(currentId.dataset.productId) : null;
    const ids = read().filter((id) => id !== currentValue).slice(0, limit);

    if (ids.length === 0) {
      container.remove();
      return;
    }

    /* `id:1 OR id:2` is the search syntax for "these exact products". Results
       come back in relevance order rather than the order asked for, which is
       acceptable here — the set is what matters, not the sequence. */
    const query = ids.map((id) => `id:${id}`).join(' OR ');
    const params = new URLSearchParams({
      q: query,
      type: 'product',
      section_id: container.dataset.sectionId,
    });

    fetch(`${container.dataset.url}?${params}`)
      .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
      .then((markup) => {
        const fresh = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('[data-recently-viewed]');
        if (!fresh || fresh.children.length === 0) {
          container.remove();
          return;
        }
        container.replaceWith(fresh);
        /* The scroll-reveal observer ran at page load, long before this markup
           existed. Handing it a node the shopper may already have scrolled past
           would leave the section invisible permanently, so reveal it outright
           — it arrives after first paint anyway. */
        fresh.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
      })
      .catch(() => container.remove());
  });
})();
